import { z } from 'zod';
import { AcubeWebhookEventSchema } from '../../fiscal/acube';
import {
  AcubeInvoiceNotificationSchema,
  AcubeInvoiceReadSchema,
  AcubePreservedDocumentSchema,
  AcubeSupplierInvoiceWebhookSchema,
} from '../../fiscal/acube-fatturapa';

/**
 * Webhook gov-it: A-Cube non mette alcun discriminatore nel body, il tipo di
 * evento è implicito nella `ApiConfiguration` registrata → viaggia nel path.
 * I payload sono le risorse ufficiali: PASSTHROUGH perché il fornitore
 * aggiunge campi senza preavviso. MAI usare .strict() qui.
 */
export const acubeWebhookParamsSchema = z.object({
  tenantSlug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/),
  event: z.string().min(1).max(80).regex(/^[a-z0-9-]+$/),
});
export type AcubeWebhookParams = z.infer<typeof acubeWebhookParamsSchema>;

/** `customer-invoice`: la risorsa `Invoice` completa. */
export const AcubeCustomerInvoiceWebhookSchema = AcubeInvoiceReadSchema.passthrough();
export type AcubeCustomerInvoiceWebhook = z.infer<typeof AcubeCustomerInvoiceWebhookSchema>;

/** `customer-notification`: notifica SdI + fattura aggiornata. */
export const AcubeCustomerNotificationWebhookSchema = z.object({
  notification: AcubeInvoiceNotificationSchema.passthrough(),
  invoice: AcubeInvoiceReadSchema.passthrough(),
}).passthrough();
export type AcubeCustomerNotificationWebhook = z.infer<typeof AcubeCustomerNotificationWebhookSchema>;

/** `invoice-status-quarantena` / `invoice-status-invoice-error`: cambio di marking (`transioned_at` è il nome ufficiale). */
export const AcubeInvoiceMarkingChangeWebhookSchema = z.object({
  uuid: z.string().min(1),
  marking: z.string(),
  previous_marking: z.string().nullable().optional(),
  notice: z.string().nullable().optional(),
  transioned_at: z.string().nullable().optional(),
}).passthrough();
export type AcubeInvoiceMarkingChangeWebhook = z.infer<typeof AcubeInvoiceMarkingChangeWebhookSchema>;

/** `legal-storage-receipt` / `legal-storage-missing-vat`: `PreservedDocument` ufficiale. */
export const AcubeLegalStorageWebhookSchema = AcubePreservedDocumentSchema.passthrough();
export type AcubeLegalStorageWebhook = z.infer<typeof AcubeLegalStorageWebhookSchema>;

/** `job` / `job-error`: `Job.JobOutput` ufficiale. */
export const AcubeJobWebhookSchema = z.object({
  uuid: z.string().min(1),
  type: z.string(),
  status: z.string(),
  parameters: z.record(z.string()).nullable().optional(),
  fiscal_id: z.string().nullable().optional(),
  downloadable: z.boolean().optional(),
  scheduled_at: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  output: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
}).passthrough();
export type AcubeJobWebhook = z.infer<typeof AcubeJobWebhookSchema>;

/** Schema del body per ciascun evento registrato (il dispatcher usa `event` dal path). */
export const ACUBE_WEBHOOK_PAYLOAD_SCHEMAS = {
  'customer-invoice': AcubeCustomerInvoiceWebhookSchema,
  'customer-notification': AcubeCustomerNotificationWebhookSchema,
  'invoice-status-quarantena': AcubeInvoiceMarkingChangeWebhookSchema,
  'invoice-status-invoice-error': AcubeInvoiceMarkingChangeWebhookSchema,
  'supplier-invoice': AcubeSupplierInvoiceWebhookSchema.passthrough(),
  'legal-storage-receipt': AcubeLegalStorageWebhookSchema,
  'legal-storage-missing-vat': AcubeLegalStorageWebhookSchema,
  job: AcubeJobWebhookSchema,
  'job-error': AcubeJobWebhookSchema,
} as const satisfies Record<z.infer<typeof AcubeWebhookEventSchema>, z.ZodTypeAny>;

export type AcubeWebhookPayloadFor<E extends keyof typeof ACUBE_WEBHOOK_PAYLOAD_SCHEMAS> =
  z.infer<(typeof ACUBE_WEBHOOK_PAYLOAD_SCHEMAS)[E]>;

export const ACUBE_WEBHOOK_DISPATCH_STATUSES = ['pending', 'done', 'failed', 'archived'] as const;
export const AcubeWebhookDispatchStatusSchema = z.enum(ACUBE_WEBHOOK_DISPATCH_STATUSES);
export type AcubeWebhookDispatchStatus = z.infer<typeof AcubeWebhookDispatchStatusSchema>;

/** Riga di `acube_webhook_events` esposta alle diagnostiche (timeline fattura, conservazione, monitor). */
export const AcubeWebhookEventRowSchema = z.object({
  id: z.string(),
  event: z.string(),
  idempotency_key: z.string(),
  resource_id: z.string().nullable(),
  signature_valid: z.boolean(),
  token_valid: z.boolean(),
  dispatch_status: AcubeWebhookDispatchStatusSchema,
  dispatched_at: z.string().nullable(),
  error_message: z.string().nullable(),
  received_at: z.string(),
  dismissed_at: z.string().nullable().optional(),
  payload: z.unknown().optional(),
});
export type AcubeWebhookEventRow = z.infer<typeof AcubeWebhookEventRowSchema>;
